if (!customElements.get("td-navigation")) {
    class TDNavigation extends HTMLElement {
        constructor() {
            super();
            
            this.DEBUG = false;
            
            // State management
            this.state = {
                activeMenu: null,
                activeMenuId: null,
                activeDetails: null,
                activeMenuType: null, // 'mega' or 'dropdown'
                isMobileMenuOpen: false
            };

            // Debug helpers
            this.debug = {
                logEvent: (e, context) => {
                    if (!this.DEBUG) return;
                    console.log('Event:', context, {
                        target: e.target,
                        currentTarget: e.currentTarget,
                        linkId: e.target.closest('[data-link]')?.dataset.link || 'none',
                        summary: e.target.closest('summary.td-nav')
                    });
                }
            };
        }

        connectedCallback() {
            // Cache DOM elements
            this.elements = {
                // Navigation elements
                menuTriggers: this.querySelectorAll('summary.td-nav'),
                dropdownTriggers: this.querySelectorAll('.site-nav__link--has-dropdown'),
                allMenuTriggers: this.querySelectorAll('summary.td-nav, .site-nav__link--has-dropdown'),
                megaMenus: this.querySelectorAll('.td-megamenu'),
                detailsElements: this.querySelectorAll('details.site-nav__details'),
                navBar: this.querySelector('.td-navigation'),
                
                // Mobile elements
                mobileHeader: this.querySelector('.td-mobile-header'),
                mobileMenu: this.querySelector("#mobile-menu"),
                mobileExit: this.querySelector("#mobile-menu-exit"),
                mobileMenuBar: this.querySelector('.td-menu--mobile'),
                slideNav: this.querySelector('.td-slide-nav'),
                navWrapper: this.querySelector('.slide-nav__overflow'),
                
                // Other UI elements
                announcementBar: document.querySelector('.announcement'),
                searchModal: document.querySelector('#SearchModal'),
                searchModalClose: document.querySelector('#SearchModal .js-modal-close'),
                cart: document.querySelector('#mobile-cart'),
                cartClose: document.querySelector('.td-mobile-cart-close'),
                popup: document.querySelector("#ps__widget")
            };

            // Initialize event listeners
            this.initializeEventListeners();
            this.initializeMobileMenu();
        }

        initializeEventListeners() {
            // Global click handler
            document.addEventListener('click', (e) => {
                this.debug.logEvent(e, 'Global click');
                
                // If clicking inside an active menu or on a menu trigger, do nothing
                if (this.state.activeMenu && (
                    this.state.activeMenu.contains(e.target) || 
                    e.target.closest('[data-link]')
                )) {
                    return;
                }
                
                // Otherwise, close all menus when clicking outside
                this.closeAllMenus();
            }, { passive: true });

            // Prevent details elements from toggling automatically
            this.elements.detailsElements.forEach(details => {
                details.addEventListener('toggle', (e) => {
                    // Only prevent the default if it's not our code doing the toggle
                    if (!this.state.activeMenu) {
                        e.preventDefault();
                    }
                });
            });

            // Attach click handlers to all menu triggers
            this.elements.allMenuTriggers.forEach(trigger => {
                trigger.addEventListener('click', (e) => this.handleMenuToggle(e));
            });
        }

        // Helper methods
        getElementByLinkId(prefix, linkId) {
            const cacheKey = `${prefix}-${linkId}`;
            if (!this._elementCache) this._elementCache = {};
            if (!this._elementCache[cacheKey]) {
                this._elementCache[cacheKey] = document.querySelector(`#${prefix}-${linkId}`);
            }
            return this._elementCache[cacheKey];
        }

        toggleActiveState(element, isActive) {
            if (!element) return;
            
            const icon = element.querySelector('svg') || element.childNodes[1];
            
            if (isActive) {
                element.classList.add('td-nav-active--text');
                if (icon) icon.classList.add('td-nav-active');
            } else {
                element.classList.remove('td-nav-active--text');
                if (icon) icon.classList.remove('td-nav-active');
            }
        }

        toggleMenu(menu, show, details = null) {
            if (!menu) return;
            
            if (show) {
                if (menu.classList.contains('td-megamenu')) {
                    menu.classList.remove('td-hide');
                }
                if (details) {
                    details.setAttribute('open', '');
                }
            } else {
                if (menu.classList.contains('td-megamenu')) {
                    menu.classList.add('td-hide');
                }
                if (details) {
                    details.removeAttribute('open');
                }
            }
        }

        closeAllMenus(exceptMenu = null) {
            // Reset mega menus
            this.elements.megaMenus.forEach(menu => {
                if (menu !== exceptMenu) {
                    menu.classList.add('td-hide');
                }
            });

            // Reset details elements
            this.elements.detailsElements.forEach(details => {
                if (!exceptMenu || !details.contains(exceptMenu)) {
                    details.removeAttribute('open');
                }
            });
            
            // Reset all menu triggers
            this.elements.allMenuTriggers.forEach(trigger => {
                if (!exceptMenu || !trigger.contains(exceptMenu)) {
                    this.toggleActiveState(trigger, false);
                }
            });
            
            // Reset state if we're doing a full reset
            if (!exceptMenu) {
                this.state.activeMenu = null;
                this.state.activeMenuId = null;
                this.state.activeDetails = null;
                this.state.activeMenuType = null;
            }
        }

        togglePopup(show) {
            if (!this.elements.popup) return;
            this.elements.popup.style.display = show ? 'block' : 'none';
        }

        toggleBodyOverflow(hide) {
            if (hide) {
                document.body.classList.add('td-hide-overflow');
            } else {
                document.body.classList.remove('td-hide-overflow');
            }
        }

        handleMenuToggle(e) {
            e.preventDefault();
            e.stopPropagation();
            this.debug.logEvent(e, 'Menu trigger click');
            
            // Get the trigger element and its link ID
            const triggerElement = e.target.closest('[data-link]');
            if (!triggerElement) return;
            
            const linkId = triggerElement.dataset.link;
            if (!linkId) {
                console.warn('No linkId found for trigger:', triggerElement);
                return;
            }
            
            // Get associated elements
            const details = triggerElement.closest('details');
            const isMegaMenu = triggerElement.classList.contains('td-nav');
            
            // Get the menu element based on type
            let menuElement;
            if (isMegaMenu) {
                menuElement = document.querySelector('#mega-' + linkId);
                if (!menuElement) {
                    console.warn('No megamenu found for linkId:', linkId);
                    return;
                }
            } else {
                menuElement = details;
            }
            
            const isCurrentlyActive = isMegaMenu 
                ? menuElement.classList.contains('td-hide') === false
                : details?.hasAttribute('open') === true;
            
            // If this is a different menu than the currently active one, or if all menus are closed
            if (menuElement !== this.state.activeMenu || !isCurrentlyActive) {
                // First close all other menus
                this.closeAllMenus(isCurrentlyActive ? menuElement : null);
                
                // If the menu is currently closed, open it
                if (!isCurrentlyActive) {
                    // Update state
                    this.state.activeMenu = menuElement;
                    this.state.activeMenuId = linkId;
                    this.state.activeDetails = details;
                    this.state.activeMenuType = isMegaMenu ? 'mega' : 'dropdown';
                    
                    // Show the menu
                    this.toggleActiveState(triggerElement, true);
                    this.toggleMenu(menuElement, true, details);
                    
                    // Toggle popup for mega menus
                    if (isMegaMenu) {
                        this.togglePopup(false);
                    }
                }
            } else {
                // Close the current menu
                this.toggleActiveState(triggerElement, false);
                this.toggleMenu(menuElement, false, details);
                this.closeAllMenus();
                
                // Toggle popup for mega menus
                if (isMegaMenu) {
                    this.togglePopup(true);
                }
            }
        }

        initializeMobileMenu() {
            // Cache mobile menu elements
            const newMobileElements = {
                mobileMenuContainer: document.querySelector('.td-mobile-menu'),
                mobileMenuToggle: document.querySelector('.js-mobile-menu-toggle'),
                mobileMenuClose: document.querySelector('.js-td-mobile-menu-close'),
                submenuTriggers: document.querySelectorAll('.td-mobile-menu__submenu-trigger'),
                backButtons: document.querySelectorAll('.td-mobile-menu__back-button')
            };
            
            // Skip if elements don't exist
            if (!newMobileElements.mobileMenuContainer || !newMobileElements.mobileMenuToggle) {
                // Legacy mobile menu handling
                if (this.elements.mobileMenu && this.elements.mobileExit) {
                    this.initializeLegacyMobileMenu();
                }
                return;
            }
            
            this.initializeNewMobileMenu(newMobileElements);
        }

        initializeLegacyMobileMenu() {
            // Mobile menu toggle (legacy)
            this.elements.mobileMenu.addEventListener('click', () => {
                const isActive = this.elements.mobileMenu.classList.contains('is-active');
                
                if (isActive) {
                    this.elements.mobileMenu.classList.add('td-mobile-menu__active');
                    this.toggleBodyOverflow(true);
                    this.state.isMobileMenuOpen = true;
                } else {
                    this.elements.mobileMenu.classList.remove('td-mobile-menu__active');
                    this.toggleBodyOverflow(false);
                    this.state.isMobileMenuOpen = false;
                }
            });

            // Mobile menu exit (legacy)
            this.elements.mobileExit.addEventListener('click', () => {
                this.elements.mobileMenu.classList.remove('td-mobile-menu__active');
                this.toggleBodyOverflow(false);
                this.state.isMobileMenuOpen = false;
                
                this.togglePopup(true);
            });
        }

        initializeNewMobileMenu(newMobileElements) {
            // New mobile menu toggle
            newMobileElements.mobileMenuToggle.addEventListener('click', () => {
                newMobileElements.mobileMenuContainer.classList.toggle('is-active');
                newMobileElements.mobileMenuToggle.classList.toggle('is-active');
                this.toggleBodyOverflow(newMobileElements.mobileMenuContainer.classList.contains('is-active'));
                this.state.isMobileMenuOpen = newMobileElements.mobileMenuContainer.classList.contains('is-active');
            });
            
            // Close mobile menu when clicking on overlay
            newMobileElements.mobileMenuClose.addEventListener('click', () => {
                newMobileElements.mobileMenuContainer.classList.add('is-closing');
                newMobileElements.mobileMenuToggle.classList.remove('is-active');
                
                setTimeout(() => {
                    newMobileElements.mobileMenuContainer.classList.remove('is-active', 'is-closing');
                    this.toggleBodyOverflow(false);
                    this.state.isMobileMenuOpen = false;
                    
                    document.querySelectorAll('.td-mobile-menu__submenu-panel.is-active')
                        .forEach(panel => panel.classList.remove('is-active'));
                }, 300);
            });
            
            const mainPanel = document.querySelector('.td-mobile-menu__main-panel');
            
            // Submenu triggers
            newMobileElements.submenuTriggers.forEach(trigger => {
                trigger.addEventListener('click', () => {
                    const submenuId = trigger.getAttribute('data-submenu-id');
                    const submenuPanel = document.getElementById(submenuId);
                    
                    if (submenuPanel) {
                        if (mainPanel) mainPanel.classList.add('has-active-submenu');
                        submenuPanel.classList.add('is-active');
                        trigger.setAttribute('aria-expanded', 'true');
                    }
                });
            });
            
            // Back buttons
            newMobileElements.backButtons.forEach(button => {
                button.addEventListener('click', () => {
                    const submenuPanel = button.closest('.td-mobile-menu__submenu-panel');
                    
                    if (submenuPanel) {
                        if (mainPanel) mainPanel.classList.remove('has-active-submenu');
                        submenuPanel.classList.remove('is-active');
                        
                        const submenuId = submenuPanel.id;
                        const trigger = document.querySelector(`[data-submenu-id="${submenuId}"]`);
                        if (trigger) trigger.setAttribute('aria-expanded', 'false');
                    }
                });
            });
            
            this.initializeMobileMenuBarInteractions();
            this.initializeMobileMenuScrollHandler(newMobileElements.mobileMenuContainer);
        }

        initializeMobileMenuScrollHandler(mobileMenuContainer) {
            if (!mobileMenuContainer) return;
            
            // Function to calculate and set the top position
            const updateTopPosition = () => {
                let announcementHeight = 0;
                let headerHeight = 72; // Default header height

                // Get the announcement bar if it exists
                if (this.elements.announcementBar && this.elements.announcementBar.getBoundingClientRect) {
                    announcementHeight = this.elements.announcementBar.getBoundingClientRect().height;
                }
                if (this.elements.mobileHeader && this.elements.mobileHeader.getBoundingClientRect) {
                    headerHeight = this.elements.mobileHeader.getBoundingClientRect().height;
                }
                
                const totalHeight = headerHeight + announcementHeight;
                const scrollPosition = window.scrollY || window.pageYOffset || 0;
                const newTopPosition = totalHeight - scrollPosition;
                
                // Apply the new top position if the container is still in the DOM
                if (mobileMenuContainer && mobileMenuContainer.style) {
                    if (newTopPosition < 0) {
                        mobileMenuContainer.style.top = `${headerHeight}px`;
                    } else {
                        mobileMenuContainer.style.top = `${newTopPosition}px`;
                    }
                }
            };
            
            // Set initial position
            updateTopPosition();
            
            if (window) {
                window.addEventListener('scroll', updateTopPosition, { passive: true });
                window.addEventListener('resize', updateTopPosition, { passive: true });
            }
            
            this._updateMobileMenuPosition = updateTopPosition;
        }

        initializeMobileMenuBarInteractions() {
            // Legacy slide nav scroll handling
            if (this.elements.slideNav) {
                this.elements.slideNav.addEventListener('click', () => {
                    if (this.elements.slideNav.classList.contains('sub-nav--is-open')) {
                        requestAnimationFrame(() => {
                            this.elements.slideNav.scrollTop = 0;
                        });
                    }
                });
            }
            
            // Mobile menu bar interactions
            if (this.elements.mobileMenuBar) {
                Array.from(this.elements.mobileMenuBar.children).forEach(menuChild => {
                    menuChild.addEventListener('click', (e) => {
                        this.debug.logEvent(e, 'Mobile menu child click');
                        
                        // Handle cart
                        if (this.elements.cart && this.elements.cart.getAttribute('aria-expanded') === 'true') {
                            this.toggleBodyOverflow(true);
                            
                            if (this.elements.cartClose) {
                                this.elements.cartClose.addEventListener('click', () => {
                                    this.toggleBodyOverflow(false);
                                    this.togglePopup(true);
                                }, { once: true });
                            }
                        }

                        // Handle search modal
                        if (this.elements.searchModal && this.elements.searchModal.classList.contains('modal--is-active')) {
                            this.toggleBodyOverflow(true);
                            
                            if (this.elements.searchModalClose) {
                                this.elements.searchModalClose.addEventListener('click', () => {
                                    this.toggleBodyOverflow(false);
                                    this.togglePopup(true);
                                }, { once: true });
                            }
                        }

                        // Close mobile menu if open
                        if (this.elements.mobileMenu && 
                            this.elements.mobileMenu.classList.contains('is-active') && 
                            !this.elements.mobileMenu.contains(e.target)) {
                            this.elements.mobileMenu.classList.remove('td-mobile-menu__active');
                            this.toggleBodyOverflow(false);
                            
                            if (window.theme && window.theme.utils && window.theme.utils.prepareTransition) {
                                window.theme.utils.prepareTransition(this.elements.navWrapper, () => {
                                    this.elements.mobileMenu.classList.remove('is-active');
                                    this.elements.navWrapper.classList.remove('js-menu--is-open');
                                });
                            }
                        }

                        // Hide popup
                        this.togglePopup(false);
                    });
                });
            }
        }
    }

    customElements.define("td-navigation", TDNavigation);
}
